import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import FieldLabelMobile from '@/domains/mobile-web/components/FieldLabelMobile/FieldLabelMobile';

export interface PickerFieldMobileProps extends Omit<ComponentProps<'button'>, 'className' | 'value' | 'type'> {
    label?: string;
    /** teks yang ditampilkan kalau sudah ada pilihan */
    value?: string;
    placeholder?: string;
    error?: string;
    helperText?: string;
    required?: boolean;
    /** ikon kanan: 'down' (seperti select) | 'right' (ke halaman lain) | custom */
    icon?: 'down' | 'right' | ReactNode;
    /** class untuk pembungkus (label + field) */
    className?: string;
    /** class tambahan untuk elemen tombol */
    buttonClassName?: string;
}

/**
 * Field yang tampilannya seperti select, tapi sebenarnya tombol.
 * Dipakai kalau tap field harus membuka modal / bottom sheet
 * atau pindah ke halaman lain (bukan dropdown native).
 */
const PickerFieldMobile = forwardRef<HTMLButtonElement, PickerFieldMobileProps>(
    (
        { label, value, placeholder, error, helperText, required, icon = 'down', className, buttonClassName, id, name, disabled, ...props },
        ref
    ) => {
        const buttonId = id ?? name;
        const hasValue = !!value;

        const renderIcon = () => {
            if (icon === 'down') return <ChevronDown className="size-5 shrink-0" />;
            if (icon === 'right') return <ChevronRight className="size-5 shrink-0" />;
            return icon;
        };

        return (
            <FieldLabelMobile
                label={label}
                htmlFor={buttonId}
                required={required}
                error={error}
                helperText={helperText}
                className={className}
            >
                <button
                    ref={ref}
                    id={buttonId}
                    name={name}
                    type="button"
                    disabled={disabled}
                    aria-invalid={!!error}
                    aria-haspopup="dialog"
                    className={cn(
                        'field-mobile inline-flex items-center justify-between gap-3 text-left',
                        error && 'field-mobile-error',
                        buttonClassName
                    )}
                    {...props}
                >
                    <span className={cn('min-w-0 flex-1 truncate', !hasValue && 'text-db-text-grey')}>
                        {hasValue ? value : placeholder}
                    </span>
                    <span className="text-black">{renderIcon()}</span>
                </button>
            </FieldLabelMobile>
        );
    }
);

PickerFieldMobile.displayName = 'PickerFieldMobile';

export default PickerFieldMobile;
